import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viCirclePerson, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function AccountList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const accountStore = useSelector((state) => state.backstage.accounts);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time accountList or searchQuery changes
  const searchResults = useSearch(accountStore.list, searchValue, ["firstname", "lastname", "email"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getAllAccounts()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && accountStore.meta.lastFetched) {
      if (accountStore.list.length >= 1) {
        navigate(`${accountStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, accountStore.list, accountStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Accounts"
        aria-label="Account list"
        id="account-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => [x.firstname, x.lastname].join(" "),
          secondary: (x) => x.email,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
        }}
        hideContentLoader={accountStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: accountStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any accounts yet",
          noResults: "No accounts in your current query",
          fetching: "Getting your accounts",
          icon: viCirclePerson,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default AccountList;
